import { useMemo, useState } from "react";
import useTranslation from "../../../client/hooks/useTranslation";
import { useLiteData } from "../../data/LiteDataProvider";
import { byMonth, feesStats, riskMetrics, rollingWinRate } from "../../lib/analytics";
import { byDay, closedOnly, equityCurve, streaks, summarize } from "../../lib/stats";
import { BucketTable, Insights, MetricGrid } from "../../ui/analytics";
import { EquityChart, PnlBars, RollingChart } from "../../ui/Charts";
import { Card, Empty, Segmented } from "../../ui/ui";
import { compact, insight, usePf, type TabProps } from "./shared";

export function OverviewTab({ trades }: TabProps) {
  const tl = useTranslation().lite;
  const t = tl.stats.overview;
  const { fmt } = useLiteData();
  const pf = usePf();
  const [mode, setMode] = useState<"equity" | "daily">("equity");

  const s = summarize(trades);
  const days = byDay(closedOnly(trades));
  const r = riskMetrics(trades);
  const f = feesStats(trades);
  const st = streaks(trades);
  const curve = useMemo(() => equityCurve(trades, fmt.locale), [trades, fmt.locale]);
  const rolling = useMemo(() => rollingWinRate(trades, fmt.locale, 20), [trades, fmt.locale]);
  const months = byMonth(trades);
  const m = t.m;

  const insights = compact([
    s.netPnl >= 0
      ? insight("good", t.insightPositive(fmt.money(s.netPnl), days.length, fmt.money(r.avgDaily)))
      : insight("bad", t.insightNegative(fmt.money(s.netPnl), days.length)),
    insight(f.ratio > 0.15 ? "bad" : "info", t.insightFees(fmt.pct(f.ratio), fmt.money(f.fees)) + (f.ratio > 0.15 ? t.insightFeesHigh : "")),
    s.wins > 0 && s.losses > 0 && insight(
      s.avgLoss > s.avgWin ? "bad" : "good",
      t.insightWinLoss(fmt.money(s.avgWin, { sign: false }), fmt.money(s.avgLoss, { sign: false }), s.avgWin >= s.avgLoss)
        + (s.avgWin < s.avgLoss ? t.insightBreakeven(fmt.pct(s.avgLoss / (s.avgWin + s.avgLoss))) : ""),
    ),
  ]);

  return (
    <>
      <Insights items={insights} />
      <MetricGrid
        title={t.keyFigures}
        metrics={[
          { label: m.net, value: fmt.money(s.netPnl), tone: s.netPnl >= 0 ? "gain" : "loss", hint: tl.common.trades(s.count) },
          { label: m.gross, value: fmt.money(f.gross), hint: m.grossHint },
          { label: m.fees, value: fmt.money(f.fees), tone: "loss", hint: m.feesHint(fmt.money(f.perTrade, { decimals: true })) },
          { label: m.winRate, value: fmt.pct(s.winRate, 1), hint: m.winRateHint(s.wins, s.losses) },
          { label: m.pf, value: pf(s.profitFactor), hint: m.pfHint },
          { label: m.expectancy, value: fmt.money(s.expectancy, { decimals: true }), tone: s.expectancy >= 0 ? "gain" : "loss" },
          { label: m.avgWin, value: fmt.money(s.avgWin), tone: "gain" },
          { label: m.avgLoss, value: fmt.money(-s.avgLoss), tone: "loss" },
          { label: m.ratio, value: s.avgLoss ? fmt.num(s.avgWin / s.avgLoss) : tl.stats.none },
          { label: m.best, value: fmt.money(s.best), tone: "gain" },
          { label: m.worst, value: fmt.money(s.worst), tone: "loss" },
          { label: m.avgDaily, value: fmt.money(r.avgDaily), tone: r.avgDaily >= 0 ? "gain" : "loss" },
          { label: m.winDays, value: fmt.pct(r.winDays / Math.max(1, days.length)), hint: m.winDaysHint(r.winDays, days.length) },
          { label: m.bestDay, value: fmt.money(r.bestDay), tone: "gain" },
          { label: m.worstDay, value: fmt.money(r.worstDay), tone: "loss" },
          { label: m.perDay, value: fmt.num(s.count / Math.max(1, days.length), 1) },
          { label: m.avgHold, value: fmt.duration(s.avgHoldMs) },
          { label: m.streaks, value: `${st.maxWin} / ${st.maxLoss}`, hint: m.streaksHint },
        ]}
      />
      <div className="grid xl:grid-cols-12 gap-5">
        <Card
          className="xl:col-span-8"
          title={mode === "equity" ? t.equityTitle : t.dailyTitle}
          action={<Segmented size="sm" value={mode} onChange={setMode} options={[{ id: "equity", label: t.cumulative }, { id: "daily", label: t.perDay }]} />}
        >
          {mode === "equity"
            ? <EquityChart data={curve} height={280} />
            : <PnlBars data={curve.map(c => ({ label: c.label, value: Math.round(c.pnl) }))} height={280} />}
        </Card>
        <Card className="xl:col-span-4" title={t.rollingTitle} subtitle={t.rollingSubtitle}>
          {rolling.length > 2 ? <RollingChart data={rolling} height={280} /> : <Empty title={t.notEnough} />}
        </Card>
      </div>
      <div className="grid xl:grid-cols-12 gap-5">
        <Card className="xl:col-span-5" title={t.monthsTitle}>
          <PnlBars data={months.map(mo => ({ label: fmt.monthShort(mo.date), value: Math.round(mo.s.netPnl) }))} height={240} />
        </Card>
        <BucketTable
          className="xl:col-span-7"
          title={t.monthsDetail}
          rows={months.map(mo => ({ key: mo.key, label: fmt.monthYear(mo.date), s: mo.s }))}
        />
      </div>
    </>
  );
}
